/**
 * Beginner mode: full-screen 'your targets' intro shown each round after the card intro
 * (`CardIntro`). Every enemy card (destroy these), with its Kill Quest, example dice that
 * roll it, and its own power. Your own card isn't shown here. The dark backdrop is up from the first frame. Each enemy card first flips over
 * where its seat is, then flies into the intro while the rest fades in; cards already face up (kept from the last round,
 * or flipped by a war) only fly in. Next sends each card back to its place on the
 * table and the overlay fades out.
 */
import { type CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { t, tn, type CardDef, type CardId, type GameView, type PlayerId, type PublicPlayer } from '@kadice/shared';
import { KillExample } from '../beginner';
import { cardImage, killerName, powerText } from '../cards';
import { tx } from '../text';
import { PName } from './Names';

const FLIP_MS = 700;
const FLY_MS = 800;

/** Portrait phones: each enemy's text sits beside its card and grows to fill the card's height. */
const SIDE_BY_SIDE = '(max-width: 800px) and (orientation: portrait)';
const TEXT_MIN_PX = 13;
const TEXT_MAX_PX = 20;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Centre-to-centre move and scale from `from` onto `to`, as a CSS transform. */
function moveOnto(from: DOMRect, to: DOMRect): string {
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  return `translate(${dx}px, ${dy}px) scale(${to.width / from.width})`;
}

const seatCard = (id: string | undefined) =>
  document.querySelector<HTMLElement>(`.opponents [data-card-of="${id}"] .card`);

/** You (with a card) and every enemy with a face-up card; null when there's no intro to show. */
export function introCards(view: GameView): { self: PublicPlayer; enemies: PublicPlayer[] } | null {
  if (view.you.spectating) return null;
  const self = view.players.find((p) => p.id === view.you.id);
  if (!self || self.card === null) return null;
  const enemies = view.players.filter((p) => p.id !== self.id && p.card !== null);
  return enemies.length > 0 ? { self, enemies } : null;
}

export function TargetsIntro({
  view,
  catalog,
  faceUp,
  onDone,
}: {
  view: GameView;
  catalog: ReadonlyMap<CardId, CardDef>;
  /** Players whose cards were already face up on the table: they don't flip. */
  faceUp: readonly PlayerId[];
  onDone: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [arriving, setArriving] = useState(() => !reducedMotion());
  const [leaving, setLeaving] = useState(false);
  const cards = introCards(view);

  // The enemy cards on the table are the ones in the intro: they stay out of sight until Next has
  // put the intro's back on them (so there is only ever one of each card to see).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const spots = [...root.querySelectorAll<HTMLElement>('[data-intro-of]')]
      .map((el) => seatCard(el.dataset.introOf))
      .filter((el): el is HTMLElement => !!el);
    spots.forEach((s) => (s.style.visibility = 'hidden'));
    return () => spots.forEach((s) => (s.style.visibility = ''));
  }, []);

  // Each enemy card flips over where it lies on the table, then flies in; the rest fades in after.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || reducedMotion()) return;
    const anims = [...root.querySelectorAll<HTMLElement>('[data-intro-of]')].map((el) => {
      const seat = seatCard(el.dataset.introOf);
      const at = seat ? moveOnto(el.getBoundingClientRect(), seat.getBoundingClientRect()) : 'scale(0.4)';
      const up = faceUp.includes(Number(el.dataset.introOf));
      return el.animate(
        [
          { transform: `${at} rotateY(${up ? 0 : 180}deg)` },
          { transform: `${at} rotateY(0deg)`, offset: FLIP_MS / (FLIP_MS + FLY_MS) },
          { transform: 'none' },
        ],
        { duration: FLIP_MS + FLY_MS, easing: 'ease-in-out' },
      );
    });
    const t = setTimeout(() => setArriving(false), FLIP_MS + FLY_MS);
    return () => {
      clearTimeout(t);
      anims.forEach((a) => a.cancel());
    };
    // Mounted per intro (keyed), so this runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Portrait phones: each text block takes the largest font size (up to TEXT_MAX_PX) that keeps it
  // within its card's height. Re-fitted when a card resizes (image load, rotation, resize).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const query = window.matchMedia(SIDE_BY_SIDE);
    const fit = () => {
      for (const row of root.querySelectorAll<HTMLElement>('.intro-target')) {
        const card = row.querySelector<HTMLElement>('.intro-card');
        const text = row.querySelector<HTMLElement>('.intro-text');
        if (!card || !text) continue;
        if (!query.matches) {
          text.style.removeProperty('--intro-fs');
          continue;
        }
        const room = card.offsetHeight;
        let lo = TEXT_MIN_PX;
        let hi = TEXT_MAX_PX;
        while (hi - lo > 0.5) {
          const mid = (lo + hi) / 2;
          text.style.setProperty('--intro-fs', `${mid}px`);
          if (text.scrollHeight <= room) lo = mid;
          else hi = mid;
        }
        text.style.setProperty('--intro-fs', `${lo}px`);
      }
    };
    fit();
    const watch = new ResizeObserver(fit);
    root.querySelectorAll('.intro-card').forEach((el) => watch.observe(el));
    query.addEventListener('change', fit);
    return () => {
      watch.disconnect();
      query.removeEventListener('change', fit);
    };
  }, []);

  // Next is never greyed out: pressed while the cards are still flying in, it just closes.
  const fly = useCallback(() => {
    if (leaving) return;
    if (arriving) return onDone();
    setLeaving(true);
    const root = rootRef.current;
    if (!root || reducedMotion()) {
      onDone();
      return;
    }
    // Each enemy card moves back onto its card on the table.
    for (const el of root.querySelectorAll<HTMLElement>('[data-intro-of]')) {
      const target = seatCard(el.dataset.introOf);
      if (!target) continue;
      const to = moveOnto(el.getBoundingClientRect(), target.getBoundingClientRect());
      el.animate([{ transform: 'none' }, { transform: to }], {
        duration: FLY_MS,
        easing: 'cubic-bezier(0.5, 0, 0.2, 1)',
        fill: 'forwards',
      });
    }
  }, [leaving, arriving, onDone]);

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(onDone, FLY_MS);
    return () => clearTimeout(t);
  }, [leaving, onDone]);

  if (!cards) return null;
  const { enemies } = cards;

  return (
    <div
      ref={rootRef}
      className={`targets-intro${arriving ? ' arriving' : ''}${leaving ? ' leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('targets.label')}
    >
      <h2 className="intro-fade">
        {t('targets.title', { round: view.phase === 'war' ? view.round + 1 : view.round })}
      </h2>
      <p className="intro-label danger intro-fade">{tn('targets.destroy', enemies.length)}</p>
      <div className="intro-targets" style={{ '--rows': enemies.length } as CSSProperties}>
        {enemies.map((p) => {
          const card = catalog.get(p.card!);
          return (
            <div key={p.id} className="intro-target">
              <div className="intro-card enemy">
                <div className="intro-flip" data-intro-of={p.id}>
                  <img src={cardImage(p.color, 'back')} alt="" className="card back" />
                  <img
                    src={cardImage(p.color, p.card!)}
                    alt={card?.name ?? t('targets.enemyCard')}
                    className="card front"
                  />
                </div>
              </div>
              {card && (
                <div className="intro-fade intro-text">
                  <PName>{p.name}</PName>
                  <span>{tx('targets.quest', { killer: <strong>{killerName(card)}</strong> })}</span>
                  <KillExample card={card} />
                  <span className="muted small">{t('targets.power', { power: powerText(card) })}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="intro-fade intro-how">{t('targets.how')}</p>
      <button className="primary intro-fade intro-next" onClick={fly}>
        {t('common.next')}
      </button>
    </div>
  );
}
